import { useEffect, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "@/components/admin/ui";
import { formatCents, dollarsToCents, centsToDollars, blankInvoiceRow } from "./billingMeta";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

export const InvoiceFormModal = ({ API, invoice, onClose, onSaved, onError }) => {
  const isEdit = !!invoice;
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(invoice?.customer_id || "");
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState(invoice?.lead_id || "");
  const [rows, setRows] = useState(invoice?.items?.map((i) => ({ ...i })) || [blankInvoiceRow()]);
  const [discountDollars, setDiscountDollars] = useState(centsToDollars(invoice?.discount_cents || 0));
  const [taxRate, setTaxRate] = useState(invoice ? String((invoice.tax_rate_bp || 0) / 100) : "0");
  const [adjustmentDollars, setAdjustmentDollars] = useState(centsToDollars(invoice?.adjustment_cents || 0));
  const [paymentTerms, setPaymentTerms] = useState(invoice?.payment_terms || "");
  const [dueDate, setDueDate] = useState(invoice?.due_date || "");
  const [notes, setNotes] = useState(invoice?.notes || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/org/billing/customers`, { withCredentials: true }).then(({ data }) => setCustomers(data.customers || [])).catch(() => {});
  }, [API]);

  useEffect(() => {
    if (!customerId) { setLeads([]); return; }
    axios.get(`${API}/org/billing/customers/${customerId}/leads`, { withCredentials: true }).then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  }, [API, customerId]);

  const updateRow = (idx, field, value) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addRow = () => setRows((r) => [...r, blankInvoiceRow()]);

  const subtotalCents = rows.reduce((sum, r) => sum + (Number(r.quantity) || 0) * (Number(r.unit_price_cents) || 0), 0);
  const discountCents = Math.min(dollarsToCents(discountDollars), subtotalCents);
  const taxableCents = Math.max(subtotalCents - discountCents, 0);
  const taxCents = Math.round(taxableCents * (parseFloat(taxRate) || 0) * 100 / 10000);
  const totalCents = Math.max(taxableCents + taxCents + dollarsToCents(adjustmentDollars), 0);

  const submit = async () => {
    if (!customerId) { setError("Choose a Customer"); return; }
    setSaving(true);
    setError("");
    const payload = {
      customer_id: customerId,
      lead_id: leadId || null,
      items: rows.map((r) => ({ description: r.description, quantity: Number(r.quantity) || 1, unit_price_cents: Number(r.unit_price_cents) || 0 })).filter((r) => r.description.trim()),
      discount_cents: dollarsToCents(discountDollars),
      tax_rate_bp: Math.round((parseFloat(taxRate) || 0) * 100),
      adjustment_cents: dollarsToCents(adjustmentDollars),
      payment_terms: paymentTerms || null,
      due_date: dueDate || null,
      notes: notes || null,
      currency: "USD",
    };
    try {
      if (isEdit) await axios.patch(`${API}/org/billing/invoices/${invoice.invoice_id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/org/billing/invoices`, payload, { withCredentials: true });
      onSaved();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg); onError?.(msg);
    } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title={isEdit ? `Edit Invoice ${invoice.display_id}` : "New Invoice"} subtitle="Draft invoices can be edited freely until sent." testId="billing-invoice-form-drawer" wide>
      <div className="space-y-5" data-testid="billing-invoice-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="billing-invoice-form-error">{error}</p>}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Customer</label>
            <select value={customerId} onChange={(e) => { setCustomerId(e.target.value); setLeadId(""); }} disabled={isEdit} data-testid="billing-invoice-customer-select" className={inputClass}>
              <option value="">Select Customer…</option>
              {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Link to Lead (optional)</label>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} disabled={isEdit || !customerId} data-testid="billing-invoice-lead-select" className={inputClass}>
              <option value="">Standalone invoice</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.display_id}{l.work_job_title ? ` — ${l.work_job_title}` : ""}{l.proposal_status === "Accepted" ? " (Accepted Proposal)" : ""}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <p className={labelClass}>Line Items</p>
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 rounded-xl border border-slate-200 p-2.5 dark:border-white/10" data-testid={`billing-invoice-item-row-${idx}`}>
              <input value={row.description} onChange={(e) => updateRow(idx, "description", e.target.value)} placeholder="Description" data-testid={`billing-invoice-item-desc-${idx}`} className="col-span-6 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <input type="number" min={1} value={row.quantity} onChange={(e) => updateRow(idx, "quantity", e.target.value)} data-testid={`billing-invoice-item-qty-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <input type="number" step="0.01" value={centsToDollars(row.unit_price_cents)} onChange={(e) => updateRow(idx, "unit_price_cents", dollarsToCents(e.target.value))} placeholder="Unit $" data-testid={`billing-invoice-item-price-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <button type="button" onClick={() => removeRow(idx)} data-testid={`billing-invoice-item-remove-${idx}`} className="col-span-2 rounded-lg border border-slate-200 text-xs font-bold text-red-500 hover:bg-red-50 dark:border-white/15">Remove</button>
            </div>
          ))}
          <button type="button" onClick={addRow} data-testid="billing-invoice-add-item-btn" className={outlineBtn}>+ Add Line Item</button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div><label className={labelClass}>Discount ($)</label><input type="number" step="0.01" value={discountDollars} onChange={(e) => setDiscountDollars(e.target.value)} data-testid="billing-invoice-discount-input" className={inputClass} /></div>
          <div><label className={labelClass}>Tax Rate (%)</label><input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="billing-invoice-tax-input" className={inputClass} /></div>
          <div><label className={labelClass}>Adjustment ($)</label><input type="number" step="0.01" value={adjustmentDollars} onChange={(e) => setAdjustmentDollars(e.target.value)} data-testid="billing-invoice-adjustment-input" className={inputClass} /></div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className={labelClass}>Payment Terms</label><input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="e.g. Due on receipt, Net 15" data-testid="billing-invoice-terms-input" className={inputClass} /></div>
          <div><label className={labelClass}>Due Date</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="billing-invoice-due-date-input" className={inputClass} /></div>
        </div>
        <div><label className={labelClass}>Notes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="billing-invoice-notes-input" className={inputClass} /></div>

        <div className="rounded-xl border border-slate-200 p-4 text-sm dark:border-white/10" data-testid="billing-invoice-totals-summary">
          <div className="flex justify-between py-1"><span className="text-slate-500 dark:text-slate-400">Subtotal</span><span className="font-semibold">{formatCents(subtotalCents)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500 dark:text-slate-400">Discount</span><span className="font-semibold">-{formatCents(discountCents)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500 dark:text-slate-400">Tax</span><span className="font-semibold">{formatCents(taxCents)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500 dark:text-slate-400">Adjustment</span><span className="font-semibold">{formatCents(dollarsToCents(adjustmentDollars))}</span></div>
          <div className="mt-1 flex justify-between border-t border-slate-200 pt-2 text-base font-extrabold dark:border-white/10"><span>Total</span><span data-testid="billing-invoice-total-preview">{formatCents(totalCents)}</span></div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="billing-invoice-form-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="billing-invoice-form-save" className={btn}>{isEdit ? "Save Draft" : "Create Draft Invoice"}</button>
        </div>
      </div>
    </DetailDrawer>
  );
};
